<div class="h-full min-h-0" *transloco="let t">
  <app-form-shell>
    <div shellHeader>
      <IStepper [steps]="formSteps()" [form]="form" (stepSelect)="goToStep($event)" clickable />
    </div>
    <div shellMain class="flex flex-col gap-6 py-8">
      @if (dspAdvertiserRequest() && dspAdvertiserRequest()!.lastUpdated !== dspAdvertiserRequest()!.createTime) {
        <div class="type-body text-muted-foreground">
          {{ t("dsp-advertiser-request-page.last_update") }}
          <span class="text-foreground font-medium">{{ dspAdvertiserRequest()!.lastUpdated | date: "short" }}</span>
        </div>
      }

      @switch (currentStepId()) {
        @case ("advertiser") {
          <div class="flex flex-col gap-4">
            <div>
              <h5 class="text-foreground text-lg font-medium">
                {{ t("dsp-advertiser-request-page.advertiser_info") }}
              </h5>
              <div class="text-muted-foreground text-xs">
                {{ t("dsp-advertiser-request-page.advertiser_info_description") }}
              </div>
            </div>
            <div class="flex flex-col gap-6">
              <IFormField>
                <label iLabel>{{ t("dsp-advertiser-request-page.advertiser_name") }}*</label>
                <div iInputGroup>
                  <input
                    size="sm"
                    iInputGroupInput
                    type="text"
                    id="advertiserName"
                    [formField]="form.advertiser.advertiserName"
                  />
                </div>
                <span iHint>{{ t("dsp-advertiser-request-page.advertiser_name_hint") }}</span>
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("dsp-advertiser-request-page.url") }}*</label>
                <div iInputGroup>
                  <input size="sm" iInputGroupInput type="text" id="url" [formField]="form.advertiser.url" />
                </div>
                <span iHint>{{ t("dsp-advertiser-request-page.url_hint") }}</span>
                <IError>
                  @for (error of form.advertiser.url().errors()!; track error.kind) {
                    {{ t(error.message!) }}
                  }
                </IError>
              </IFormField>
              <div class="flex flex-col gap-1">
                <label iLabel>{{ t("dsp-advertiser-request-page.industry") }}*</label>
                <ISelectMenu popoverWidth="300" [formField]="form.advertiser.industry">
                  <ISelectTrigger>
                    <ISelectValue [placeholder]="t('dsp-advertiser-request-page.industry_placeholder')" />
                  </ISelectTrigger>
                  <ISelectContent>
                    @for (industry of industries; track industry) {
                      <ISelectOption [value]="industry">
                        {{ industry }}
                      </ISelectOption>
                    }
                  </ISelectContent>
                </ISelectMenu>
              </div>
              <IFormField class="flex-1">
                <label iLabel>{{ t("dsp-advertiser-request-page.business_registration_number") }}</label>
                <div iInputGroup>
                  <input
                    size="sm"
                    iInputGroupInput
                    type="text"
                    id="businessRegistrationNumber"
                    [formField]="form.advertiser.businessRegistrationNumber"
                  />
                </div>
                <span iHint>{{ t("dsp-advertiser-request-page.business_registration_number_hint") }}</span>
              </IFormField>
            </div>
          </div>
        }
        @case ("permissions") {
          <div class="flex flex-col gap-4">
            <div>
              <h5 class="text-foreground text-lg font-medium">
                {{ t("dsp-advertiser-request-page.permitted_users") }}
              </h5>
              <div class="text-muted-foreground text-xs">
                {{ t("dsp-advertiser-request-page.permitted_users_description") }}
              </div>
            </div>

            <IFormField class="flex-1">
              <div class="flex flex-col gap-1">
                @for (email of form.permissions.grantedEmails().value(); track email) {
                  <div class="text-foreground flex items-center gap-2 text-sm">
                    <span>{{ email }}</span>
                    <button iButton type="button" variant="ghost" (click)="removeGrantedEmail(email)">
                      <IIcon name="trash" />
                    </button>
                  </div>
                }
              </div>
              @if (isAddingGrantedEmail()) {
                <div class="flex items-center gap-2">
                  <div iInputGroup>
                    <input size="sm" iInputGroupInput type="text" id="grantedEmails" [formField]="grantedEmailForm" />
                  </div>
                  <button iButton type="button" variant="secondary" (click)="cancelGrantedEmail()">
                    {{ t("common.cancel") }}
                  </button>
                  <button iButton type="button" variant="main" (click)="addGrantedEmail()">
                    {{ t("common.add") }}
                  </button>
                </div>
              } @else {
                <button iButton type="button" variant="secondary" (click)="isAddingGrantedEmail.set(true)">
                  {{ t("common.add") }}
                </button>
              }
              <IError>{{ t("profile-settings.invalid_email") }}</IError>
            </IFormField>
            @if (!form.permissions.grantedEmails().value().length) {
              <IError>{{ t("dsp-advertiser-request-page.at_least_one_permitted_user") }}</IError>
            }
          </div>
        }
        @case ("billing") {
          <div class="flex flex-col gap-4">
            <div>
              <h5 class="text-foreground text-lg font-medium">
                {{ t("dsp-advertiser-request-page.billing_details") }}
              </h5>
              <div class="text-muted-foreground text-xs">
                {{ t("dsp-advertiser-request-page.billing_details_description") }}
              </div>
            </div>

            @if (!dspAdvertiserRequest()) {
              <div class="flex items-center gap-2">
                <IToggle
                  [checked]="autofillFromBillingInfoChecked()"
                  (checkedChange)="toggleAutofillAndFillFormFromCustomer()"
                />
                <span class="text-muted-foreground text-sm font-medium">{{
                  t("dsp-advertiser-request-page.autofill_from_billing_info")
                }}</span>
              </div>
            }

            <div class="flex flex-col gap-4">
              <IFormField class="flex-1">
                <label iLabel>{{ t("dsp-advertiser-request-page.billing_currency") }}*</label>
                <ISelectMenu fitTriggerWidth [formField]="form.billing.currency">
                  <ISelectTrigger>
                    <ISelectValue [placeholder]="t('common.select')">
                      <div *iSelectValue>{{ selectedCurrencyLabel() }}</div>
                    </ISelectValue>
                  </ISelectTrigger>
                  <ISelectContent>
                    @for (currency of currencies; track currency.value) {
                      <ISelectOption [value]="currency.value">
                        {{ currency.label }}
                      </ISelectOption>
                    }
                  </ISelectContent>
                </ISelectMenu>
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("dsp-advertiser-request-page.billing_email") }}*</label>
                <div iInputGroup>
                  <input size="sm" iInputGroupInput type="text" id="email" [formField]="form.billing.email" />
                </div>
                <span iHint>{{ t("dsp-advertiser-request-page.billing_email_hint") }}</span>
                <IError>{{ t("profile-settings.invalid_email") }}</IError>
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("dsp-advertiser-request-page.contact_name") }}*</label>
                <div iInputGroup>
                  <input
                    size="sm"
                    iInputGroupInput
                    type="text"
                    id="contactName"
                    [formField]="form.billing.contactName"
                  />
                </div>
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("login.company_name_field") }}*</label>
                <div iInputGroup>
                  <input
                    size="sm"
                    iInputGroupInput
                    type="text"
                    id="companyName"
                    [formField]="form.billing.companyName"
                  />
                </div>
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("billing-customer.address_line_1") }}*</label>
                <div iInputGroup>
                  <input
                    size="sm"
                    iInputGroupInput
                    type="text"
                    id="addressLine1"
                    [formField]="form.billing.addressLine1"
                  />
                </div>
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("billing-customer.address_line_2") }}</label>
                <div iInputGroup>
                  <input
                    size="sm"
                    iInputGroupInput
                    type="text"
                    id="addressLine2"
                    [formField]="form.billing.addressLine2"
                  />
                </div>
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("billing-customer.country") }}*</label>
                <ISelectMenu
                  fitTriggerWidth
                  [(searchQuery)]="countrySearch"
                  [value]="form.billing.addressCountry().value()"
                  (valueChange)="selectAddressCountry($event!)"
                >
                  <ISelectTrigger>
                    <ISelectValue [placeholder]="t('common.select')">
                      <div *iSelectValue="let value">{{ selectedCountry()?.name ?? value }}</div>
                    </ISelectValue>
                  </ISelectTrigger>
                  <ISelectContent>
                    @for (country of filteredCountries(); track country.code) {
                      <ISelectOption [value]="country.code">
                        {{ country.name }}
                      </ISelectOption>
                    }
                  </ISelectContent>
                </ISelectMenu>
              </IFormField>
              @if (states().length) {
                <IFormField class="flex-1">
                  <label iLabel>{{ t("billing-customer.state") }}*</label>
                  <ISelectMenu fitTriggerWidth [(searchQuery)]="stateSearch" [formField]="form.billing.state">
                    <ISelectTrigger>
                      <ISelectValue [placeholder]="t('common.select')" />
                    </ISelectTrigger>
                    <ISelectContent>
                      @for (state of filteredStates(); track state) {
                        <ISelectOption [value]="state">
                          {{ state }}
                        </ISelectOption>
                      }
                    </ISelectContent>
                  </ISelectMenu>
                </IFormField>
              }
              <IFormField class="flex-1">
                <label iLabel>{{ t("billing-customer.city") }}*</label>
                <div iInputGroup>
                  <input size="sm" iInputGroupInput type="text" id="city" [formField]="form.billing.city" />
                </div>
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("billing-customer.postal_code") }}*</label>
                <div iInputGroup>
                  <input size="sm" iInputGroupInput type="text" id="postalCode" [formField]="form.billing.postalCode" />
                </div>
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("login.phone_number") }}*</label>
                <iPhoneInput [formField]="form.billing.phone" />
              </IFormField>
              <IFormField class="flex-1">
                <label iLabel>{{ t("dsp-advertiser-request-page.vat_number") }}</label>
                <div iInputGroup>
                  <input size="sm" iInputGroupInput type="text" id="vat" [formField]="form.billing.vat" />
                </div>
              </IFormField>
            </div>
          </div>
        }
      }
    </div>
    <div shellFooter class="flex items-center justify-between gap-3">
      <button iButton variant="secondary" type="button" [disabled]="submitLoading()" (click)="cancel()">
        {{ t("common.cancel") }}
      </button>
      <div class="flex items-center gap-3">
        @if (!isFirstStep()) {
          <button iButton variant="secondary" type="button" [disabled]="submitLoading()" (click)="back()">
            {{ t("common.back") }}
          </button>
        }
        @if (isLastStep()) {
          <button
            iButton
            type="button"
            [disabled]="!canGoNext() || !isAdmin()"
            [loading]="submitLoading()"
            [iTooltip]="form().invalid() ? t('dsp-advertiser-request-page.submit_request_tooltip') : undefined"
            iTooltipPlacement="top-center"
            (click)="next()"
          >
            {{ formSubmitLabel() }}
          </button>
        } @else {
          <button iButton type="button" [disabled]="!canGoNext()" (click)="next()">
            {{ t("common.next") }}
          </button>
        }
      </div>
    </div>
  </app-form-shell>
</div>
